// The sponsor's pages, Stage 5 piece 6. Words: OBSERVATIONS_SCAN_WORDS.md rev 3, "The sponsor".
// Mockup: MOCKUP_CONTRACTOR_ACCESS.html rev 3 (approved 2026-10-12).
// Opened from the sponsor's own email link, no sign-in, like Review And Decide:
//   ?cv=CV-0001&k=...&co=alltech        Confirm Your Contractor: name, four commitments, Confirm or Not Mine (once)
//   ?cv=CV-0001&d=2&k=...&co=alltech    Daily Check: No, or Yes with one line (once), Job Done, Extend 1 Day

import "./contractor.css";
import { callServer } from "./firebase";
import { SPONSOR_COMMITMENTS, clockText, usShort, type SponsorView } from "./contractor";

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export interface SponsorState {
  name: string;
  ticks: string[];
  note: string;
  busy: boolean;
  error: string;
  /** A line after Job Done or Extend. */
  done: string;
}

export const EMPTY_SPONSOR: SponsorState = { name: "", ticks: [], note: "", busy: false, error: "", done: "" };

function head(eyebrow: string, title: string): string {
  return `<div class="ca-hd"><div class="e">${esc(eyebrow)}</div><div class="t">${esc(title)}</div></div>`;
}

function details(v: SponsorView): string {
  const job = `${v.jobDays} ${v.jobDays === 1 ? "day" : "days"} at ${v.siteName}${v.endsOn ? `, ends ${usShort(v.endsOn)}` : ""}`;
  return `<dl class="ca-dl"><dt>Contractor</dt><dd>${esc([v.contractor.name, v.contractor.company].filter(Boolean).join(" · "))}</dd>
    <dt>Their supervisor</dt><dd>${esc(v.contractor.supervisor)}</dd><dt>Phone</dt><dd>${esc(v.contractor.phone)}</dd>
    <dt>Job</dt><dd>${esc(job)}</dd><dt>Program check</dt><dd>${esc(v.checkLine)}</dd></dl>`;
}

/** Confirm Your Contractor. */
export function confirmHtml(v: SponsorView, s: SponsorState): string {
  let h = `${head(`SAFETAG ESP · ${v.visitId}`, "Confirm Your Contractor")}<div class="ca-body">`;
  if (v.status !== "Waiting") {
    const end =
      v.status === "On Site" || v.status === "Done"
        ? `<div class="ca-done">You confirmed ${esc(v.contractor.name)}.</div><p>Recorded as ${esc(v.sponsorName)}${v.confirmedAt ? ` at ${esc(clockText(v.confirmedAt))}` : ""}. You are responsible for them on site. The daily check comes by email each job day at 3 PM.</p>`
        : v.status === "Not Mine"
          ? `<div class="ca-done n">You tapped Not Mine.</div><p>${esc(v.contractor.name)} was told not to start. The site's Alerts contacts were told.</p>`
          : `<div class="ca-done n">This request expired.</div><p>No answer in 4 hours. ${esc(v.contractor.name)} was told not to start. They can send a new one from any label.</p>`;
    return `${h}${end}${details(v)}</div>`;
  }
  const ticks = SPONSOR_COMMITMENTS.map((c, i) => {
    const on = s.ticks.includes(c);
    return `<button class="ca-tick${on ? "" : " off"}" type="button" data-sp-tick="${i}" aria-pressed="${on}"><span class="bx">${on ? "✓" : ""}</span><span>${esc(c)}</span></button>`;
  }).join("");
  const all = SPONSOR_COMMITMENTS.every((c) => s.ticks.includes(c)) && s.name.trim().length > 0;
  h += `<div class="ca-who">You are confirming as <b>${esc(v.sponsorEmail)}</b>.</div>
    <label class="ca-f" for="sp-name">Your name<input id="sp-name" value="${esc(s.name)}" autocomplete="name" maxlength="80"></label>
    ${details(v)}
    <div class="ca-sec">Your commitments</div>${ticks}
    <div class="ca-two"><button class="ca-no" type="button" data-sp="notMine"${s.busy ? " disabled" : ""}>Not Mine</button><button class="ca-yes" type="button" data-sp="confirm"${all && !s.busy ? "" : " disabled"}>Confirm</button></div>
    ${s.error ? `<div class="ca-err" role="status">${esc(s.error)}</div>` : ""}
    <p class="ca-small">Type your name and tick each commitment to confirm. If you did not bring them in, tap Not Mine. Expires ${esc(clockText(v.expiresAt))}.</p></div>`;
  return h;
}

/** The Daily Check for one job day. */
export function dailyHtml(v: SponsorView, s: SponsorState): string {
  const d = v.day;
  const company = v.contractor.company || v.contractor.name;
  let h = `${head(`SAFETAG ESP · ${v.visitId}${d ? ` · DAY ${d.day} OF ${v.jobDays}` : ""}`, "Daily Check")}<div class="ca-body">`;
  if (!d) return `${h}<div class="ca-err">This day's check was not found.</div></div>`;
  h += `<p class="ca-q">Any injury, near miss, damage or unsafe work with ${esc(company)} today?</p>`;
  if (d.answer) {
    h += `<div class="ca-done${d.answer === "yes" ? " n" : ""}">Day ${d.day}: ${d.answer === "yes" ? "Yes" : "No"}.</div>`;
    if (d.answer === "yes") h += `<p>${esc(d.note)}</p><p class="ca-small">The ESP Manager was told.</p>`;
    if (d.at) h += `<p class="ca-small">Recorded at ${esc(clockText(d.at))}. An answer cannot be changed.</p>`;
  } else {
    h += `<div class="ca-two"><button class="ca-no" type="button" data-sp="yes"${s.busy ? " disabled" : ""}>Yes</button><button class="ca-yes" type="button" data-sp="no"${s.busy ? " disabled" : ""}>No</button></div>
      <textarea id="sp-note" class="ca-ta" maxlength="300" placeholder="If Yes: one line. The ESP Manager is told." aria-label="What happened">${esc(s.note)}</textarea>`;
  }
  if (v.status === "On Site") {
    h += `<div class="ca-two"><button class="ca-plain" type="button" data-sp="jobDone"${s.busy ? " disabled" : ""}>Job Done</button><button class="ca-plain" type="button" data-sp="extend"${s.busy || !v.canExtend ? " disabled" : ""}>Extend 1 Day</button></div>
      <p class="ca-small">Job Done stops these emails. Extend adds a day, up to 30. The job ends ${esc(usShort(v.endsOn))}.</p>`;
  } else h += `<p class="ca-small">This visit is ${esc(v.status)}. No more daily checks.</p>`;
  if (s.done) h += `<div class="ca-done">${esc(s.done)}</div>`;
  if (s.error) h += `<div class="ca-err" role="status">${esc(s.error)}</div>`;
  return `${h}</div>`;
}

export function mountSponsorPage(root: HTMLElement, link: { visitId: string; day: number | null; code: string; company: string }): void {
  document.title = `${link.visitId} · SafeTag`;
  document.body.classList.add("guest");
  let view: SponsorView | null = null;
  let s: SponsorState = { ...EMPTY_SPONSOR, ticks: [] };
  let failed = "";
  const args = { cv: link.visitId, k: link.code, co: link.company, ...(link.day ? { d: link.day } : {}) };

  function draw(): void {
    const inner = view
      ? view.kind === "confirm"
        ? confirmHtml(view, s)
        : dailyHtml(view, s)
      : `${head(`SAFETAG ESP · ${link.visitId}`, link.day ? "Daily Check" : "Confirm Your Contractor")}<div class="ca-body">${failed ? `<div class="ca-err">${esc(failed)}</div><div class="ca-two"><button class="ca-yes" type="button" data-load-retry>Try Again</button></div>` : "<p>Opening…</p>"}</div>`;
    root.innerHTML = `<div class="ca ca-page">${inner}</div>`;
  }

  // A load error offers Try Again, never words alone (NAV_AUDIT.md item 12). Opened from an email.
  root.addEventListener("click", (e) => {
    if (!view && (e.target as HTMLElement).closest("[data-load-retry]")) {
      failed = "";
      draw();
      void load();
    }
  });

  async function load(): Promise<void> {
    try {
      view = await callServer<SponsorView>("loadSponsorLink", args);
      failed = "";
    } catch (e) {
      failed = e instanceof Error ? e.message : String(e);
    }
    draw();
  }

  async function answer(act: string): Promise<void> {
    if (!view || s.busy) return;
    if (act === "yes" && !s.note.trim()) {
      s = { ...s, error: "Write one line about what happened. The ESP Manager is told." };
      draw();
      root.querySelector<HTMLTextAreaElement>("#sp-note")?.focus();
      return;
    }
    s = { ...s, busy: true, error: "", done: "" };
    draw();
    try {
      view = await callServer<SponsorView>("answerSponsorLink", { ...args, act, name: s.name, commitments: s.ticks, note: s.note });
      const done = act === "jobDone" ? "Job Done. The daily checks stop." : act === "extend" ? `Extended. The job now ends ${usShort(view.endsOn)}.` : "";
      s = { ...s, busy: false, done };
    } catch (e) {
      s = { ...s, busy: false, error: `Not recorded. ${e instanceof Error ? e.message : String(e)}` };
    }
    draw();
    window.scrollTo(0, 0);
  }

  root.addEventListener("click", (ev) => {
    const t = ev.target as HTMLElement;
    const tick = t.closest<HTMLElement>("[data-sp-tick]");
    if (tick) {
      const c = SPONSOR_COMMITMENTS[Number(tick.dataset.spTick)];
      if (!c) return;
      s = { ...s, ticks: s.ticks.includes(c) ? s.ticks.filter((x) => x !== c) : [...s.ticks, c] };
      draw();
      return;
    }
    const b = t.closest<HTMLButtonElement>("[data-sp]");
    if (!b || b.disabled) return;
    void answer(b.dataset.sp ?? "");
  });
  root.addEventListener("input", (ev) => {
    const el = ev.target as HTMLInputElement | HTMLTextAreaElement;
    if (el.id === "sp-note") s.note = el.value;
    else if (el.id === "sp-name") {
      s.name = el.value;
      const btn = root.querySelector<HTMLButtonElement>('[data-sp="confirm"]');
      if (btn) btn.disabled = s.busy || !(SPONSOR_COMMITMENTS.every((c) => s.ticks.includes(c)) && s.name.trim().length > 0);
    }
  });

  draw();
  void load();
}
